/**
 * TuneIndexPickerModal.js
 * Search and pick a book-index / cd-index content page for product.index_page_id.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { queryMatchesSearchText } from '../utils/siteSearch';
import './ProductPickerModal.css';

const INDEX_PAGE_TYPES = new Set(['book-index', 'cd-index']);

function pageSearchHaystack(page) {
  return [
    page.page_id,
    page.title,
    page.description,
    page.page_type,
  ]
    .filter(Boolean)
    .join(' ');
}

const TuneIndexPickerModal = ({ isOpen, onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSearchTerm('');
    setLoadError('');

    const fetchPages = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/content/pages?_=${Date.now()}`, {
          method: 'GET',
          cache: 'no-cache',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
        });
        if (!response.ok) {
          const t = await response.text();
          throw new Error(t || `HTTP ${response.status}`);
        }
        const data = await response.json();
        const indexRows = (Array.isArray(data) ? data : []).filter((p) =>
          INDEX_PAGE_TYPES.has(String(p.page_type || '').toLowerCase().trim()),
        );
        const byId = new Map();
        for (const p of indexRows) {
          const id = String(p.page_id || '').trim();
          if (!id || byId.has(id)) continue;
          byId.set(id, p);
        }
        setPages([...byId.values()]);
      } catch (err) {
        console.error('TuneIndexPickerModal fetch:', err);
        setLoadError('Could not load pages. You may need content.view permission or to sign in again.');
        setPages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPages();
  }, [isOpen]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim();
    if (!q) return pages;
    return pages.filter((p) =>
      queryMatchesSearchText(pageSearchHaystack(p).toLowerCase(), q),
    );
  }, [pages, searchTerm]);

  const handleSelect = (page) => {
    onSelect(page);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="product-picker-overlay" onClick={onClose}>
      <div className="product-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="product-picker-header">
          <h3>Select tune index page</h3>
          <button type="button" className="close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="product-picker-search">
          <input
            type="text"
            placeholder="Search by title, description, or page ID…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            autoFocus
          />
        </div>

        <div className="product-picker-content">
          {loading ? (
            <div className="loading-state">Loading index pages…</div>
          ) : loadError ? (
            <div className="empty-state">
              <p>{loadError}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <p>No book-index or cd-index pages found</p>
              {searchTerm.trim() ? <p className="hint">Try a different search term</p> : null}
            </div>
          ) : (
            <div className="products-list">
              {filtered.map((page) => (
                <div
                  key={page.page_id}
                  className="product-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelect(page)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelect(page);
                    }
                  }}
                >
                  <div className="product-info" style={{ width: '100%' }}>
                    <div className="product-header">
                      <span className="product-name">{page.title || page.page_id}</span>
                      <span className="product-id">ID: {page.page_id}</span>
                    </div>
                    {page.description ? (
                      <div className="product-meta" style={{ marginTop: '8px' }}>
                        <span style={{ fontSize: '0.85em', color: 'var(--color-text-secondary)' }}>
                          {page.description.substring(0, 150)}
                          {page.description.length > 150 ? '…' : ''}
                        </span>
                      </div>
                    ) : null}
                    <div className="product-footer">
                      <span className="product-type">
                        {page.page_type === 'cd-index' ? '💿 CD index' : '📖 Book index'}
                      </span>
                      {page.updated_at ? (
                        <span style={{ fontSize: '0.75em', color: 'var(--color-text-secondary)' }}>
                          Updated: {new Date(page.updated_at).toLocaleDateString()}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="product-picker-footer">
          <p className="footer-hint">Click a row to set this product&apos;s tune index page (stores the page ID).</p>
        </div>
      </div>
    </div>
  );
};

export default TuneIndexPickerModal;
